import { useEffect, useMemo, useRef, useState } from "react";

import { ApiError } from "@/api/client";
import { usePackFileQuery, usePackFilesQuery } from "@/api/packFiles";
import {
  useCandidateContentsQuery,
  usePreviewCandidatesQuery,
} from "@/api/preview";
import { usePreviewRunEventQuery } from "@/api/previewInsights";
import {
  buildClassificationState,
  classificationDocumentId,
  effectiveStatements,
  effectiveWeights,
  frozenFileText,
  parseClassifierReading,
  parseMainConcernAnswers,
  runCandidateOf,
  trackDisplayName,
  type ClassificationState,
  type ClassifierReading,
  type FinishedEvaluation,
  type FlowStep,
  type MainConcernAnswers,
  type MainConcernDocuments,
  type MainConcernRound,
  type StepState,
  type TrackScore,
} from "@/lib/labClassificationState";
import type { LabRunData } from "@/lib/labModel";
import { describePosition, readContentSlot } from "@/lib/previewDecisions";
import { cn } from "@/lib/cn";
import { Badge } from "@/ui/Badge";
import { Card } from "@/ui/Card";
import { Tooltip } from "@/ui/Tooltip";

import { TEXT_READ_GAP_MS } from "../LabConversation";
import { LabTabStrip } from "./LabTabStrip";

/** The weight document of a main-concern agent; a pinned slot cites the flow setting instead. */
const WEIGHTS_SUFFIX = "_diagnosis.yaml";

/** The pack root every pack-file path (archive and Files endpoint alike) starts under. */
const PACK_ROOT = "conversation_agent";

const ANSWERS_READ_RETRIES = 1;

export const NO_CLASSIFICATION_YET =
  "No classification yet: the main concern stage has not finished.";

const MARKS = { yes: "✓", no: "✗", unknown: "?" } as const satisfies Record<
  StepState,
  string
>;

function points(value: number | null): string | null {
  return value === null
    ? null
    : `${value < 0 ? "−" : "+"}${Math.abs(value)} pts`;
}

function percent(score: number | null, max: number | null): string | null {
  return score === null || max === null || max <= 0
    ? null
    : `${Math.round((score / max) * 100)}%`;
}

type StateTabProps = {
  projectId: string;
  run: LabRunData;
  throughTurn: number | null;
};

/** Points and statements come from the documents the run used, never the top bar's choice. */
export function LabClassificationStateTab(props: StateTabProps) {
  // Keyed by run so documents and answers never carry over to another run.
  return <StateView key={props.run.previewRunRef} {...props} />;
}

type DocumentsReading = {
  documents: ReadonlyMap<string, MainConcernDocuments>;
  /** One line per reason points are missing or qualified; empty when all is well. */
  notes: readonly string[];
};

const NO_DOCUMENTS: DocumentsReading = { documents: new Map(), notes: [] };

function StateView({ projectId, run, throughTurn }: StateTabProps) {
  const base = useMemo(
    () => buildClassificationState({ lab: run.lab, throughTurn }),
    [run.lab, throughTurn],
  );
  const weightIdsKey = [
    ...new Set(
      base.rounds.flatMap((round) =>
        round.weightsDocument !== null &&
        round.weightsDocument.id.endsWith(WEIGHTS_SUFFIX)
          ? [round.weightsDocument.id]
          : [],
      ),
    ),
  ].join("|");

  // Stable identity: the loader below re-runs its effects on a new array.
  const weightIds = useMemo(
    () => (weightIdsKey === "" ? [] : weightIdsKey.split("|")),
    [weightIdsKey],
  );
  const [reading, setReading] = useState<DocumentsReading>(NO_DOCUMENTS);
  const [answers, setAnswers] = useState<
    ReadonlyMap<string, MainConcernAnswers>
  >(new Map());
  const [readings, setReadings] = useState<
    ReadonlyMap<string, ClassifierReading>
  >(new Map());
  const state = useMemo(
    () =>
      buildClassificationState({
        lab: run.lab,
        throughTurn,
        documents: reading.documents,
        answers,
        readings,
      }),
    [run.lab, throughTurn, reading.documents, answers, readings],
  );
  // The main-concern answers first, then each screening's and evaluation's sub-steps.
  const readsKey = [
    ...state.rounds.flatMap((round) =>
      round.classificationEventRef === null
        ? []
        : [`answers:${round.classificationEventRef}`],
    ),
    ...state.evaluations.flatMap((evaluation) =>
      evaluation.classificationEventRef === null
        ? []
        : [`steps:${evaluation.classificationEventRef}`],
    ),
  ].join("|");
  const reads = useMemo(
    () =>
      readsKey === ""
        ? []
        : readsKey.split("|").map((entry): BodyRead => {
            const [kind, eventRef] = entry.split(":") as [
              BodyRead["kind"],
              string,
            ];
            return { kind, eventRef };
          }),
    [readsKey],
  );

  return (
    <div className="space-y-3">
      {state.position !== null ? (
        <p className="text-sm">
          <span className="text-muted-foreground">Now: </span>
          <span dir="auto">{describePosition(state.position)}</span>
        </p>
      ) : null}
      {weightIdsKey !== "" ? (
        <EffectiveDocuments
          projectId={projectId}
          run={run}
          weightIds={weightIds}
          onReading={setReading}
        />
      ) : null}
      {reads.length > 0 ? (
        <BodyQueue
          projectId={projectId}
          previewRunRef={run.previewRunRef}
          reads={reads}
          onRead={(read, value) =>
            read.kind === "answers"
              ? setAnswers((held) =>
                  new Map(held).set(read.eventRef, value as MainConcernAnswers),
                )
              : setReadings((held) =>
                  new Map(held).set(read.eventRef, value as ClassifierReading),
                )
          }
        />
      ) : null}
      <StateBody state={state} notes={reading.notes} />
    </div>
  );
}

const MAIN_CONCERN_TAB = "main_concern";

function StateBody({
  state,
  notes,
}: {
  state: ClassificationState;
  notes: readonly string[];
}) {
  const [picked, setPicked] = useState(MAIN_CONCERN_TAB);
  if (state.empty) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        {NO_CLASSIFICATION_YET}
      </p>
    );
  }
  const tabs = [
    ...(state.rounds.length > 0
      ? [{ id: MAIN_CONCERN_TAB, label: "Main Concern" }]
      : []),
    ...state.evaluations.map((evaluation) => ({
      id: evaluation.classifierId,
      label: trackDisplayName(evaluation.classifierId),
    })),
  ];
  const active = tabs.some((tab) => tab.id === picked)
    ? picked
    : (tabs[0]?.id ?? MAIN_CONCERN_TAB);
  const evaluation = state.evaluations.find((e) => e.classifierId === active);
  const diagnosed = state.evaluations.filter(
    (e) => e.passed === true && !e.setAside,
  );
  const tabbed = state.evaluations.length > 0;
  return (
    <>
      <FlowSteps flow={state.flow} />
      {diagnosed.length > 0 ? (
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">Diagnosis results</h4>
          <ul
            aria-label="Diagnosis results"
            className="flex flex-wrap gap-1 text-xs"
          >
            {diagnosed.map((e) => (
              <li
                key={e.classifierId}
                className="rounded-md bg-success px-2 py-0.5 text-success-foreground"
              >
                <code dir="ltr" className="font-mono">
                  {e.classifierId}
                </code>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {tabbed ? (
        <LabTabStrip
          idPrefix="lab-classification"
          ariaLabel="Classifications"
          tabs={tabs}
          value={active}
          onChange={setPicked}
        />
      ) : null}
      <div
        // The enclosing inspector tab owns the one tabpanel role.
        role={tabbed ? "group" : undefined}
        id="lab-classification-panel"
        aria-labelledby={tabbed ? `lab-classification-${active}` : undefined}
      >
        {evaluation !== undefined ? (
          <Card className="space-y-3 p-3">
            <EvaluationView evaluation={evaluation} />
          </Card>
        ) : (
          <Card className="space-y-3 p-3">
            <h4 className="text-sm font-semibold">
              Main concern classifications
            </h4>
            {notes.map((note) => (
              <p
                key={note}
                role="status"
                className="text-xs text-muted-foreground"
              >
                {note}
              </p>
            ))}
            {state.rounds.map((round) => (
              <RoundView key={round.decisionEventRef} round={round} />
            ))}
          </Card>
        )}
      </div>
    </>
  );
}

const FLOW_MARKS = { done: "✓", current: "●", next: "○" } as const;

/** Where the conversation has been, is, and goes next, as legacy Reliv's Flow Steps. */
function FlowSteps({ flow }: { flow: readonly FlowStep[] }) {
  if (flow.length === 0) {
    return null;
  }
  return (
    <div className="space-y-1">
      <h4 className="text-sm font-semibold">Flow steps</h4>
      <ul
        aria-label="Flow steps"
        className="flex flex-wrap items-center gap-1 text-xs"
      >
        {flow.map((step) => (
          <li
            key={step.id}
            data-status={step.status}
            aria-current={step.status === "current" ? "step" : undefined}
            className={cn(
              "rounded-full border px-2 py-0.5",
              step.status === "done" &&
                "border-transparent bg-success text-success-foreground",
              step.status === "current" &&
                "border-primary font-medium text-foreground",
              step.status === "next" && "border-border text-muted-foreground",
            )}
          >
            <span aria-hidden="true">{FLOW_MARKS[step.status]}</span>
            <span className="sr-only">{step.status}</span> {step.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The run's candidate is matched on revision number plus attested hash, never the top bar's choice. */
function EffectiveDocuments({
  projectId,
  run,
  weightIds,
  onReading,
}: {
  projectId: string;
  run: LabRunData;
  weightIds: readonly string[];
  onReading: (reading: DocumentsReading) => void;
}) {
  const candidates = usePreviewCandidatesQuery(projectId);
  const runHash = run.view?.summary?.identity.candidateContentHash ?? null;
  const candidate = candidates.data
    ? runCandidateOf(
        candidates.data.items,
        run.candidateRevision ?? null,
        runHash,
      )
    : null;
  const contents = useCandidateContentsQuery(
    projectId,
    candidate?.candidateId ?? null,
  );
  const packFiles = usePackFilesQuery(projectId);

  const [loaded, setLoaded] = useState<ReadonlyMap<string, DocumentLoad>>(
    new Map(),
  );
  const reportRef = useRef(onReading);
  useEffect(() => {
    reportRef.current = onReading;
  });

  const mountedBase = packFiles.data?.basePack?.revision ?? null;
  const frozenBase = candidate?.basePackRevision ?? null;
  const notes = useMemo(() => {
    if (candidates.isPending) {
      return ["Reading the run's test version."];
    }
    if (candidates.isError) {
      return ["Points are not shown: the test versions could not be listed."];
    }
    if (candidate === null) {
      return [
        "Points are not shown: the run's test version could not be identified.",
      ];
    }
    if (contents.isError) {
      return [
        "Points are not shown: the run's test version could not be read.",
      ];
    }
    const out: string[] = [];
    const loads = weightIds.map((id) => loaded.get(id));
    if (contents.isPending || loads.some((load) => load === undefined)) {
      out.push("Reading the weights.");
    }
    for (const load of loads) {
      if (load?.problem === "unreadable") {
        out.push("Points are not shown: the weight document is unreadable.");
      } else if (load?.problem === "missing") {
        out.push("Points are not shown: no weight document was found.");
      } else if (load?.problem === "error") {
        out.push("Points are not shown: the base pack could not be read.");
      }
    }
    if (
      mountedBase !== null &&
      frozenBase !== null &&
      mountedBase !== frozenBase
    ) {
      out.push(
        "The base pack may differ from the one this test version was frozen on.",
      );
    }
    return [...new Set(out)];
  }, [
    candidates.isPending,
    candidates.isError,
    candidate,
    contents.isError,
    contents.isPending,
    loaded,
    weightIds,
    mountedBase,
    frozenBase,
  ]);

  useEffect(() => {
    const documents = new Map<string, MainConcernDocuments>();
    for (const [id, load] of loaded) {
      if (load.documents !== null) {
        documents.set(id, load.documents);
      }
    }
    reportRef.current({ documents, notes });
  }, [loaded, notes]);

  if (contents.data === undefined) {
    return null;
  }
  return (
    <>
      {weightIds.map((id) => (
        <DocumentLoader
          key={id}
          projectId={projectId}
          configId={id}
          families={contents.data.families}
          onLoad={(load) => setLoaded((held) => new Map(held).set(id, load))}
        />
      ))}
    </>
  );
}

type DocumentLoad = {
  documents: MainConcernDocuments | null;
  problem: "missing" | "unreadable" | "error" | null;
};

/** Reads one weight document's base files and merges them under the candidate's; renders nothing. */
function DocumentLoader({
  projectId,
  configId,
  families,
  onLoad,
}: {
  projectId: string;
  configId: string;
  families: Parameters<typeof frozenFileText>[0];
  onLoad: (load: DocumentLoad) => void;
}) {
  const statementsId = classificationDocumentId(configId);
  const weightsOverlay = frozenFileText(families, configId);
  const statementsOverlay = frozenFileText(families, statementsId);
  const baseWeights = usePackFileQuery(projectId, `${PACK_ROOT}/${configId}`);
  const baseStatements = usePackFileQuery(
    projectId,
    `${PACK_ROOT}/${statementsId}`,
  );

  // A base that has no such file answers 404: that is "no base text", not a failure.
  const settled = (query: typeof baseWeights) =>
    !query.isPending &&
    (!query.isError ||
      (query.error instanceof ApiError && query.error.status === 404));
  const failed = (query: typeof baseWeights) =>
    query.isError && !settled(query);
  const ready = settled(baseWeights) && settled(baseStatements);
  const error = failed(baseWeights) || failed(baseStatements);
  const weightsBase = baseWeights.data?.defaultContent ?? null;
  const statementsBase = baseStatements.data?.defaultContent ?? null;

  useEffect(() => {
    if (error) {
      onLoad({ documents: null, problem: "error" });
      return;
    }
    if (!ready) {
      return;
    }
    // A candidate file whose text was withheld is not "absent": the base must not stand in for it.
    const weights =
      weightsOverlay === null
        ? { weights: null, problem: "unreadable" as const }
        : effectiveWeights(weightsOverlay ?? null, weightsBase);
    onLoad(
      weights.weights === null
        ? { documents: null, problem: weights.problem }
        : {
            documents: {
              weights: weights.weights,
              stepTexts:
                statementsOverlay === null
                  ? null
                  : effectiveStatements(
                      statementsOverlay ?? null,
                      statementsBase,
                    ),
            },
            problem: null,
          },
    );
    // `onLoad` is a fresh closure per render; the texts are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ready,
    error,
    weightsOverlay,
    weightsBase,
    statementsOverlay,
    statementsBase,
  ]);
  return null;
}

type BodyRead = { eventRef: string; kind: "answers" | "steps" };

/**
 * Reads the classification result bodies (the main-concern answers, then each
 * screening's and evaluation's sub-steps) one at a time, a gap apart (upstream
 * Liv answers a burst with 429), each retried once; renders nothing while they
 * load and a plain error once one still fails.
 */
function BodyQueue({
  projectId,
  previewRunRef,
  reads,
  onRead,
}: {
  projectId: string;
  previewRunRef: string;
  reads: readonly BodyRead[];
  onRead: (
    read: BodyRead,
    value: MainConcernAnswers | ClassifierReading,
  ) => void;
}) {
  // Settled by key, not by position: a round that appears between two reads must not shift the queue.
  const [settledKeys, setSettledKeys] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const [failures, setFailures] = useState<ReadonlySet<BodyRead["kind"]>>(
    new Set(),
  );
  const keyOf = (read: BodyRead) => `${read.kind}:${read.eventRef}`;
  const current = reads.find((read) => !settledKeys.has(keyOf(read))) ?? null;
  return (
    <>
      {current !== null ? (
        <BodyReader
          key={`${current.kind}:${current.eventRef}`}
          projectId={projectId}
          previewRunRef={previewRunRef}
          read={current}
          onValue={(value) => onRead(current, value)}
          onSettled={(ok) => {
            if (!ok) {
              setFailures((held) => new Set(held).add(current.kind));
            }
            // The next read waits one gap: the throttle is per request, not per turn.
            window.setTimeout(
              () => setSettledKeys((held) => new Set(held).add(keyOf(current))),
              TEXT_READ_GAP_MS,
            );
          }}
        />
      ) : null}
      {failures.has("answers") ? (
        <p role="alert" className="text-xs text-destructive">
          The classifier&apos;s answers could not be read, so the selected
          track&apos;s score and each step&apos;s result are not shown.
        </p>
      ) : null}
      {failures.has("steps") ? (
        <p role="alert" className="text-xs text-destructive">
          The steps of the classifiers could not be read, so some sub-steps are
          not shown.
        </p>
      ) : null}
    </>
  );
}

function slotText(detail: unknown, field: string): string | null {
  return typeof detail === "object" && detail !== null
    ? (readContentSlot((detail as Record<string, unknown>)[field])?.inline ??
        null)
    : null;
}

/** One result-body read, retried {@link ANSWERS_READ_RETRIES} time; reports whether it ended usable. */
function BodyReader({
  projectId,
  previewRunRef,
  read: target,
  onValue,
  onSettled,
}: {
  projectId: string;
  previewRunRef: string;
  read: BodyRead;
  onValue: (value: MainConcernAnswers | ClassifierReading) => void;
  onSettled: (ok: boolean) => void;
}) {
  const read = usePreviewRunEventQuery(
    projectId,
    previewRunRef,
    target.eventRef,
    { enabled: true },
  );
  const detail = (read.data?.event as Record<string, unknown> | undefined)?.[
    "detail"
  ];
  const inline = slotText(detail, "result");
  const parsed =
    inline === null
      ? null
      : target.kind === "answers"
        ? parseMainConcernAnswers(inline)
        : parseClassifierReading(inline, slotText(detail, "rationale"));
  const { isError, isPending, errorUpdatedAt, refetch } = read;
  const attempts = useRef(0);
  const settledRef = useRef(false);
  const report = useRef({ onValue, onSettled });
  useEffect(() => {
    report.current = { onValue, onSettled };
  });

  useEffect(() => {
    if (settledRef.current) {
      return;
    }
    if (parsed !== null) {
      settledRef.current = true;
      report.current.onValue(parsed);
      report.current.onSettled(true);
      return;
    }
    // An answered read with no usable body (withheld, or not the step map) will not improve on retry.
    const unusable = !isPending && !isError;
    if (unusable) {
      settledRef.current = true;
      report.current.onSettled(false);
      return;
    }
    if (isError) {
      if (attempts.current < ANSWERS_READ_RETRIES) {
        // Counted when the retry starts, so a rerender that cancels the timer
        // only reschedules it.
        const timer = window.setTimeout(() => {
          attempts.current += 1;
          void refetch();
        }, TEXT_READ_GAP_MS);
        return () => window.clearTimeout(timer);
      }
      settledRef.current = true;
      report.current.onSettled(false);
    }
    return undefined;
  }, [parsed, isError, isPending, errorUpdatedAt, refetch]);
  return null;
}

function RoundView({ round }: { round: MainConcernRound }) {
  const selected = round.tracks.find((track) => track.selected);
  const top = Math.max(1, ...round.tracks.map((track) => track.score ?? 0));
  return (
    <div className="space-y-3 border-t border-border pt-3 first:border-t-0 first:pt-0">
      <p className="text-sm font-medium">
        Round {round.round} · Selected track:{" "}
        <span dir="auto">{trackDisplayName(round.selectedTrack)}</span>
      </p>
      <ul
        className="space-y-1"
        aria-label={`Round ${round.round} track scores`}
      >
        {round.tracks.map((track) => (
          <li key={track.track} className="flex items-center gap-2 text-sm">
            <span className="w-32 shrink-0 truncate" dir="auto">
              {trackDisplayName(track.track)}
            </span>
            <span
              className="h-2 flex-1 overflow-hidden rounded-full bg-muted"
              aria-hidden="true"
            >
              <span
                className={cn(
                  "block h-full rounded-full",
                  track.selected ? "bg-primary" : "bg-muted-foreground/50",
                )}
                style={{ width: `${((track.score ?? 0) / top) * 100}%` }}
              />
            </span>
            <span dir="ltr" className="w-8 shrink-0 text-end tabular-nums">
              {track.score ?? "–"}
            </span>
            {track.selected ? <Badge tone="accent">Selected</Badge> : null}
          </li>
        ))}
      </ul>
      {selected !== undefined && selected.steps.length > 0 ? (
        <StepGrid track={selected} />
      ) : null}
      {round.tracks.map((track) =>
        track.steps.length > 0 ? (
          <TrackCard key={track.track} round={round} track={track} />
        ) : null,
      )}
    </div>
  );
}

function StepGrid({ track }: { track: TrackScore }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground">
        Selected track: {trackDisplayName(track.track)}
      </p>
      <ul className="flex flex-wrap gap-1" aria-label="Selected track criteria">
        {track.steps.map((step) => {
          const chip = (
            <li
              key={step.step}
              className={cn(
                "rounded-md border border-border px-2 py-0.5 text-xs",
                step.state === "no" && "bg-destructive/10 text-destructive",
                step.state === "yes" && "bg-success text-success-foreground",
              )}
            >
              <span aria-hidden="true">{MARKS[step.state]}</span>
              <span className="sr-only">{step.state}</span> {step.step}
            </li>
          );
          return step.text !== null ? (
            <Tooltip
              key={step.step}
              content={<span dir="auto">{step.text}</span>}
            >
              {chip}
            </Tooltip>
          ) : (
            chip
          );
        })}
      </ul>
    </div>
  );
}

function TrackCard({
  round,
  track,
}: {
  round: MainConcernRound;
  track: TrackScore;
}) {
  const share = percent(track.score, track.max);
  return (
    <Card className="space-y-2 p-3">
      <p className="flex flex-wrap items-baseline gap-2 text-sm font-medium">
        <span dir="auto">{trackDisplayName(track.track)}</span>
        {track.score !== null && track.max !== null ? (
          <span dir="ltr" className="tabular-nums">
            {track.score} / {track.max}
            {share !== null ? ` (${share})` : ""}
          </span>
        ) : null}
      </p>
      {track.score !== null && track.max !== null && track.max > 0 ? (
        <div
          role="progressbar"
          aria-label={`${trackDisplayName(track.track)} score`}
          aria-valuemin={0}
          aria-valuemax={track.max}
          aria-valuenow={track.score}
          className="h-1.5 overflow-hidden rounded-full bg-muted"
        >
          <div
            className="h-full rounded-full bg-primary"
            style={{
              width: `${Math.min(100, Math.max(0, (track.score / track.max) * 100))}%`,
            }}
          />
        </div>
      ) : null}
      <p className="text-xs text-muted-foreground">
        Round {round.round} → {trackDisplayName(round.selectedTrack)}
      </p>
      <ul className="space-y-1 text-sm">
        {track.steps.map((step) => (
          <li
            key={step.step}
            className={cn(step.state === "no" && "text-destructive")}
          >
            <span aria-hidden="true">{MARKS[step.state]}</span>
            <span className="sr-only">{step.state}</span>{" "}
            <code dir="ltr" className="font-mono text-xs">
              {step.step}
            </code>
            {step.text !== null ? <span dir="auto"> — {step.text}</span> : null}
            {points(step.points) !== null ? (
              <span dir="ltr" className="ms-1 text-xs">
                {points(step.points)}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function EvaluationView({ evaluation }: { evaluation: FinishedEvaluation }) {
  const [showReasoning, setShowReasoning] = useState(false);
  const reading = evaluation.reading;
  return (
    <div className="space-y-3">
      <p className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-semibold" dir="auto">
          {trackDisplayName(evaluation.classifierId)}
        </span>
        {evaluation.passed === null ? (
          <Badge tone="outline">Verdict not recorded</Badge>
        ) : (
          <Badge tone={evaluation.passed ? "success" : "outline"}>
            {evaluation.passed ? "Passed" : "Not passed"}
          </Badge>
        )}
        {evaluation.setAside ? <Badge tone="outline">Not applied</Badge> : null}
        {evaluation.turnNumber !== null ? (
          <span className="text-xs text-muted-foreground">
            turn {evaluation.turnNumber}
          </span>
        ) : null}
        {reading?.hasReasoning ? (
          <button
            type="button"
            aria-expanded={showReasoning}
            onClick={() => setShowReasoning(!showReasoning)}
            className="ms-auto text-xs text-primary underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            <span aria-hidden="true">{showReasoning ? "− " : "+ "}</span>
            reasoning
          </button>
        ) : null}
      </p>
      {reading === null && evaluation.classificationEventRef !== null ? (
        <p role="status" className="text-xs text-muted-foreground">
          Reading the steps…
        </p>
      ) : null}
      {reading?.groups.map((group) => (
        <section key={group.step} className="space-y-1">
          <h5 className="text-xs font-semibold text-muted-foreground">
            {group.step}
          </h5>
          <ul className="space-y-1 text-sm">
            {group.subSteps.map((sub) => (
              <li
                key={sub.id}
                className={cn(
                  "rounded-md px-2 py-1",
                  sub.passed
                    ? "bg-success text-success-foreground"
                    : "bg-destructive/10 text-destructive",
                )}
              >
                <span aria-hidden="true">{sub.passed ? "✓" : "✗"}</span>
                <span className="sr-only">
                  {sub.passed ? "met" : "not met"}
                </span>{" "}
                <code dir="ltr" className="font-mono text-xs">
                  {sub.id}
                </code>
                {showReasoning && sub.reasoning !== null ? (
                  <p dir="auto" className="mt-1 text-xs text-foreground">
                    {sub.reasoning}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ))}
      {evaluation.criteria.length > 0 ? (
        <div className="space-y-1 border-t border-border pt-3">
          <h5 className="text-xs font-semibold text-muted-foreground">
            Thresholds
          </h5>
          <ul className="space-y-1 text-sm">
            {evaluation.criteria.map((criterion) => (
              <li
                key={criterion.criterionId}
                className={cn(!criterion.passed && "text-destructive")}
              >
                <span aria-hidden="true">{criterion.passed ? "✓" : "✗"}</span>
                <span className="sr-only">
                  {criterion.passed ? "met" : "not met"}
                </span>{" "}
                <span dir="auto">{criterion.label}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
